"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.transportLayer = void 0;
const buildings_1 = require("../config/buildings");
const EXCEDENTE_THRESHOLD = 5;
const MINIBOT_CAPACITY = 10;
// BFS pathfinding — deterministic (explores neighbors in fixed order: up, right, down, left)
function bfs(grid, start, end, width, height) {
    if (start.x === end.x && start.y === end.y)
        return [];
    const visited = new Set();
    const queue = [{ pos: start, path: [] }];
    visited.add(`${start.x},${start.y}`);
    const directions = [
        { dx: 0, dy: -1 }, // up
        { dx: 1, dy: 0 }, // right
        { dx: 0, dy: 1 }, // down
        { dx: -1, dy: 0 }, // left
    ];
    while (queue.length > 0) {
        const { pos, path } = queue.shift();
        for (const dir of directions) {
            const nx = pos.x + dir.dx;
            const ny = pos.y + dir.dy;
            const key = `${nx},${ny}`;
            if (nx < 0 || ny < 0 || nx >= width || ny >= height)
                continue;
            if (visited.has(key))
                continue;
            const row = grid[ny];
            if (!row || row[nx] !== '#')
                continue; // only walk on available cells (corridors)
            visited.add(key);
            const newPath = [...path, { x: nx, y: ny }];
            if (nx === end.x && ny === end.y)
                return newPath;
            queue.push({ pos: { x: nx, y: ny }, path: newPath });
        }
    }
    return null;
}
// Find building center (first walkable cell adjacent to building)
function findAdjacentWalkable(grid, building, boardWidth, boardHeight) {
    for (let dy = -1; dy <= building.height; dy++) {
        for (let dx = -1; dx <= building.width; dx++) {
            const x = building.x + dx;
            const y = building.y + dy;
            if (x < 0 || y < 0 || x >= boardWidth || y >= boardHeight)
                continue;
            if (grid[y] && grid[y][x] === '#')
                return { x, y };
        }
    }
    return null;
}
exports.transportLayer = {
    name: 'transport',
    tickRate: 2,
    execute(state, _tick) {
        const trans = state.layers.transport;
        const grid = state.grid.layout;
        const width = state.grid.width;
        const height = state.grid.height;
        // Sort minibots by id for determinism
        const minibots = [...trans.minibots].sort((a, b) => a.id.localeCompare(b.id));
        const buildings = [...state.buildings];
        for (let i = 0; i < minibots.length; i++) {
            const bot = minibots[i];
            if (bot.state === 'idle') {
                // Find building with excedent
                const sortedBuildings = [...buildings].sort((a, b) => a.id.localeCompare(b.id));
                let sourceBuilding = null;
                let sourceResource = '';
                for (const b of sortedBuildings) {
                    if (!b.isEnabled)
                        continue;
                    const config = buildings_1.allBuildings[b.name];
                    if (!config)
                        continue;
                    if (config.type !== 'mining' && config.type !== 'factory')
                        continue;
                    for (const [resource, amount] of Object.entries(b.contains)) {
                        if (amount > EXCEDENTE_THRESHOLD) {
                            sourceBuilding = b;
                            sourceResource = resource;
                            break;
                        }
                    }
                    if (sourceBuilding)
                        break;
                }
                if (sourceBuilding) {
                    const sourcePos = findAdjacentWalkable(grid, sourceBuilding, width, height);
                    if (sourcePos) {
                        const path = bfs(grid, { x: bot.x, y: bot.y }, sourcePos, width, height);
                        if (path !== null) {
                            minibots[i] = Object.assign(Object.assign({}, bot), { state: 'moving_to_source', path, targetBuildingId: sourceBuilding.id, carrying: null });
                        }
                    }
                }
            }
            else if (bot.state === 'moving_to_source') {
                // Move one step
                if (bot.path.length > 0) {
                    const nextStep = bot.path[0];
                    minibots[i] = Object.assign(Object.assign({}, bot), { x: nextStep.x, y: nextStep.y, path: bot.path.slice(1) });
                }
                else {
                    // Arrived at source — pick up resources
                    const sourceBuilding = buildings.find(b => b.id === bot.targetBuildingId);
                    if (sourceBuilding) {
                        // Find which resource to pick
                        let pickupResource = '';
                        let pickupAmount = 0;
                        for (const [resource, amount] of Object.entries(sourceBuilding.contains)) {
                            if (amount > EXCEDENTE_THRESHOLD) {
                                pickupResource = resource;
                                pickupAmount = Math.min(MINIBOT_CAPACITY, amount);
                                break;
                            }
                        }
                        if (pickupResource && pickupAmount > 0) {
                            // Remove from source building
                            const bIdx = buildings.findIndex(b => b.id === sourceBuilding.id);
                            buildings[bIdx] = Object.assign(Object.assign({}, sourceBuilding), { contains: Object.assign(Object.assign({}, sourceBuilding.contains), { [pickupResource]: sourceBuilding.contains[pickupResource] - pickupAmount }) });
                            minibots[i] = Object.assign(Object.assign({}, bot), { state: 'moving_to_dest', carrying: { resource: pickupResource, amount: pickupAmount }, path: [], targetBuildingId: null });
                        }
                        else {
                            minibots[i] = Object.assign(Object.assign({}, bot), { state: 'idle', path: [], targetBuildingId: null });
                        }
                    }
                    else {
                        minibots[i] = Object.assign(Object.assign({}, bot), { state: 'idle', path: [], targetBuildingId: null });
                    }
                }
            }
            else if (bot.state === 'moving_to_dest') {
                if (!bot.carrying) {
                    minibots[i] = Object.assign(Object.assign({}, bot), { state: 'idle', path: [], targetBuildingId: null });
                    continue;
                }
                if (bot.path.length === 0 && !bot.targetBuildingId) {
                    // Find destination
                    const resource = bot.carrying.resource;
                    const sortedBuildings = [...buildings].sort((a, b) => a.id.localeCompare(b.id));
                    let destBuilding = null;
                    // Priority 1: factory that needs this resource as input
                    for (const b of sortedBuildings) {
                        if (!b.isEnabled)
                            continue;
                        const config = buildings_1.allBuildings[b.name];
                        if (!config || config.type !== 'factory')
                            continue;
                        const levelConfig = config.levels[b.level];
                        if (!levelConfig)
                            continue;
                        if (resource in levelConfig.costs && resource !== 'energy') {
                            destBuilding = b;
                            break;
                        }
                    }
                    // Priority 2: storage that accepts this resource
                    if (!destBuilding) {
                        for (const b of sortedBuildings) {
                            const config = buildings_1.allBuildings[b.name];
                            if (!config || config.type !== 'storage')
                                continue;
                            const levelConfig = config.levels[b.level];
                            if (!levelConfig)
                                continue;
                            if (resource in levelConfig.stores) {
                                const current = b.contains[resource] || 0;
                                const max = levelConfig.stores[resource];
                                if (current < max) {
                                    destBuilding = b;
                                    break;
                                }
                            }
                        }
                    }
                    if (destBuilding) {
                        const destPos = findAdjacentWalkable(grid, destBuilding, width, height);
                        if (destPos) {
                            const path = bfs(grid, { x: bot.x, y: bot.y }, destPos, width, height);
                            if (path !== null) {
                                minibots[i] = Object.assign(Object.assign({}, bot), { path, targetBuildingId: destBuilding.id });
                                continue;
                            }
                        }
                    }
                    // No destination found — revert pickup
                    const sourceBuilding = buildings.find(b => b.id === bot.targetBuildingId);
                    if (sourceBuilding) {
                        const bIdx = buildings.findIndex(b => b.id === sourceBuilding.id);
                        buildings[bIdx] = Object.assign(Object.assign({}, sourceBuilding), { contains: Object.assign(Object.assign({}, sourceBuilding.contains), { [bot.carrying.resource]: (sourceBuilding.contains[bot.carrying.resource] || 0) + bot.carrying.amount }) });
                    }
                    minibots[i] = Object.assign(Object.assign({}, bot), { state: 'idle', carrying: null, path: [], targetBuildingId: null });
                }
                else if (bot.path.length > 0) {
                    // Move one step
                    const nextStep = bot.path[0];
                    minibots[i] = Object.assign(Object.assign({}, bot), { x: nextStep.x, y: nextStep.y, path: bot.path.slice(1) });
                }
                else {
                    // Arrived at destination — deliver
                    const destBuilding = buildings.find(b => b.id === bot.targetBuildingId);
                    if (destBuilding && bot.carrying) {
                        const bIdx = buildings.findIndex(b => b.id === destBuilding.id);
                        buildings[bIdx] = Object.assign(Object.assign({}, destBuilding), { contains: Object.assign(Object.assign({}, destBuilding.contains), { [bot.carrying.resource]: (destBuilding.contains[bot.carrying.resource] || 0) + bot.carrying.amount }) });
                    }
                    minibots[i] = Object.assign(Object.assign({}, bot), { state: 'idle', carrying: null, path: [], targetBuildingId: null });
                }
            }
        }
        return Object.assign(Object.assign({}, state), { buildings, layers: Object.assign(Object.assign({}, state.layers), { transport: Object.assign(Object.assign({}, trans), { minibots }) }) });
    },
};



//////////////////
// WEBPACK FOOTER
// ../engine/dist/layers/transport.js
// module id = +GY5
// module chunks = 0